import React from "react";
import _ from "lodash";
import { FiRotateCcw } from "react-icons/fi";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";
import ConfigValueReadonly from "components/control-center/auto-pilot/config/ConfigValueReadonly";
import DiffIcon from "components/common/DiffIcon";
import { TYPE } from "components/ops-mgmt/account/custom-first-look/Constants";

import {
  DISPLAYTYPE,
  CHANGETYPE,
  groupDiff,
  parsedDiffTitle,
  idRegex,
  getDisplayType,
  getDisplayTypeColor,
} from "helpers/Compare";
import { getPathType, getValueByPath } from "helpers/Rollback";

import SimpleTooltip from "./SimpleTooltip";
import Checkbox from "./Checkbox";
import { buttonActionClassGray } from "helpers/StyleClass";

class ConfigDiffView extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      sections: [],
      sectionList: [],
      totalCount: -1,
      isGrouped: false,
      diffCount: null,

      isRollBackMode: false,
      hideList: [],
    };

    this.handleUpdateDiffState = this.handleUpdateDiffState.bind(this);
    this.handleRollBackModeChange = this.handleRollBackModeChange.bind(this);
    this.handleHideAllChange = this.handleHideAllChange.bind(this);
    this.handleHideRules = this.handleHideRules.bind(this);
    this.handleParsedTitle = this.handleParsedTitle.bind(this);
    this.handlePrepareDiffData = this.handlePrepareDiffData.bind(this);
  }

  componentDidMount() {
    this.handleUpdateDiffState();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.diffs !== this.props.diffs ||
      prevProps.groupRule !== this.props.groupRule
    ) {
      this.handleUpdateDiffState();
    }
  }

  handleUpdateDiffState() {
    const { diffs, groupRule } = this.props;
    const { sections, totalCount, isGrouped, diffCount } =
      this.handlePrepareDiffData({
        diffs,
        groupRule,
      });

    this.setState({
      sections,
      sectionList: this.parseSectionToList(sections),
      totalCount,
      isGrouped,
      diffCount,
      isRollBackMode: false,
      hideList: [],
    });
  }

  handleRollBackModeChange(val) {
    this.setState({ isRollBackMode: val });
  }

  handleHideAllChange() {
    this.setState((prevState) => ({
      hideList:
        prevState.sectionList.length === prevState.hideList.length
          ? []
          : [...prevState.sectionList],
    }));
  }

  handleHideRules(key) {
    this.setState((prevState) => {
      const { hideList } = prevState;
      if (hideList.includes(key)) {
        return { hideList: hideList.filter((k) => k !== key) };
      }
      return { hideList: [...hideList, key] };
    });
  }

  handleParsedTitle(diffs) {
    return diffs.map((d) => {
      const title = parsedDiffTitle(d);
      if (title == null) return d;
      return { ...d, title };
    });
  }

  handlePrepareDiffData({ diffs, groupRule }) {
    const isGroupRuleValid =
      !_.isNil(groupRule) && groupRule.key && groupRule.groups;

    const isDisplayByKey = this.isDisplayByKey(diffs);

    if (isDisplayByKey) {
      // keyed by type [Custom First Look Rules]
      const sections = Object.entries(diffs).map(([type, typeDiffs]) => {
        const parsed = this.handleParsedTitle(typeDiffs);

        if (isGroupRuleValid) {
          const grouped = groupDiff(parsed, groupRule);
          return {
            key: type,
            items: grouped,
            count: Object.keys(grouped).length,
            diffCount: typeDiffs.length,
          };
        }

        return {
          key: type,
          items: parsed,
          count: parsed.length,
          diffCount: typeDiffs.length,
        };
      });

      return {
        sections,
        totalCount: _.sumBy(sections, "count"),
        diffCount: _.sumBy(sections, "diffCount"),
        isGrouped: isGroupRuleValid,
      };
    }

    // non-keyed
    const parsed = this.handleParsedTitle(diffs);

    if (isGroupRuleValid) {
      const grouped = groupDiff(parsed, groupRule);
      return {
        sections: [{ items: grouped, count: Object.keys(grouped).length }],
        totalCount: Object.keys(grouped).length,
        diffCount: diffs.length,
        isGrouped: true,
      };
    }

    return {
      sections: [{ items: parsed, count: parsed.length }],
      totalCount: parsed.length,
      diffCount: diffs.length,
      isGrouped: false,
    };
  }

  parseSectionToList(sections) {
    return _.reduce(
      sections,
      (acc, { items }, index) => {
        acc.push(..._.keys(items).map((key) => `${index}-${key}`));
        return acc;
      },
      []
    );
  }

  isDisplayByKey(data) {
    if (!_.isPlainObject(data)) return false;
    const keyList = _.keys(data);
    return _.every(keyList, (key) => _.values(TYPE).includes(key));
  }

  render() {
    const {
      title = "",
      handleRollBack,
      height = "400px",
      groupRule,
      aliasKey = "",
      refData = [],
    } = this.props;

    const {
      sections,
      sectionList,
      totalCount,
      diffCount,
      isGrouped,
      isRollBackMode,
      hideList,
    } = this.state;

    return (
      <div className="flex flex-col" style={{ maxHeight: height }}>
        <div className="border-b-2 border-gray-400 pb-1 text-sm font-semibold text-gray-600">
          <div className="uppercase">
            {title !== "" ? `${title} ` : ""}Config Changes{" "}
          </div>
          <div className="flex gap-2">
            {totalCount > 0 && (
              <div className="truncate mr-auto flex-1 self-end">
                <span className="font-semibold text-blue-600">
                  {totalCount}
                </span>
                {isGrouped ? " rules" : " items"}
                {isGrouped && (
                  <>
                    {" for "}
                    <span className="font-semibold text-blue-600">
                      {diffCount}
                    </span>{" "}
                    changes
                  </>
                )}
              </div>
            )}
            {handleRollBack && totalCount > 0 && (
              <label className="rounded flex cursor-pointer items-center">
                <Checkbox
                  isChecked={isRollBackMode}
                  onChange={this.handleRollBackModeChange}
                ></Checkbox>
                <span className="ml-1 align-middle text-sm font-semibold text-gray-600 hover:text-gray-700">
                  Roll back
                </span>
              </label>
            )}
            {isGrouped && totalCount > 0 && (
              <button
                className={buttonActionClassGray}
                onClick={this.handleHideAllChange}
              >
                {hideList.length === sectionList.length ? "Expand" : "Collapse"}{" "}
                All
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden text-sm">
          {totalCount === 0 && (
            <div className="p-4 text-center text-gray-600">
              No changes found.
            </div>
          )}
          {sections.map(({ key, items, count }, index) => {
            if (count === 0) return null;

            return (
              <React.Fragment key={key || `section-${index}`}>
                {key && (
                  <div className="mt-1 pl-2 font-semibold text-gray-600">
                    {key} (<span className="text-blue-600">{count}</span>
                    {" of "}
                    {totalCount} rules)
                  </div>
                )}

                {isGrouped ? (
                  <GroupDiffView
                    refData={refData && key ? refData[key] : refData}
                    aliasKey={aliasKey}
                    groupedDiffs={items}
                    groups={groupRule.groups}
                    handleRollBack={
                      handleRollBack && key
                        ? (val) => handleRollBack({ ...val, configType: key })
                        : handleRollBack
                    }
                    index={index}
                    hideList={hideList}
                    handleHideRules={this.handleHideRules}
                    isRollBackMode={isRollBackMode}
                  />
                ) : (
                  items.map((diff, i) => (
                    <ConfigDiffItem
                      key={`${diff.path}-${i}`}
                      diff={diff}
                      handleRollBack={
                        handleRollBack && key
                          ? (val) => handleRollBack({ ...val, configType: key })
                          : handleRollBack
                      }
                      isRollBackMode={isRollBackMode}
                    />
                  ))
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }
}

class ConfigDiffItem extends React.PureComponent {
  render() {
    const { handleRollBack, isRollBackMode, diff } = this.props;
    const { path, from, to, type, title } = diff;

    let lastEle = "";
    let parseTitle = title;
    if (_.isNil(title)) {
      const pathList = path.split(".");
      lastEle = pathList.pop();
      parseTitle = pathList
        .map((str) => (idRegex.test(str) ? str : _.startCase(str)))
        .join(" / ");
    }

    return (
      <div className="border-b -mx-4 mb-2 px-4 pb-2 last:border-b-0">
        <div
          className={`${getDisplayTypeColor(
            type
          )} border-l-4 pl-2 font-semibold text-blue-800`}
        >
          <div className="flex items-center gap-1">
            {handleRollBack && isRollBackMode && (
              <button
                className="hover:text-blue-600"
                onClick={() => handleRollBack(diff)}
              >
                <FiRotateCcw></FiRotateCcw>
              </button>
            )}
            {parseTitle === "" ? "[ROOT]" : parseTitle}
          </div>
          <div>{idRegex.test(lastEle) ? lastEle : _.startCase(lastEle)}</div>
        </div>

        <div className="flex text-gray-600">
          <div
            className="flex break-all"
            style={{ width: "calc(50% - 0.5rem)" }}
          >
            <div>
              <ConfigValueReadonly
                valueType={type}
                configValue={from}
              ></ConfigValueReadonly>
            </div>
          </div>
          <div className="self-center px-2 text-lg">
            <DiffIcon type={type} />
          </div>

          {getDisplayType(type) !== DISPLAYTYPE.NO_CHANGE && (
            <div
              className="flex break-all"
              style={{ width: "calc(50% - 0.5rem)" }}
            >
              <div className="font-semibold text-gray-800">
                <ConfigValueReadonly
                  valueType={type}
                  configValue={to}
                  fromValue={from}
                ></ConfigValueReadonly>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

class GroupDiffView extends React.PureComponent {
  parseGroupKey(key) {
    const result = getPathType(key);
    if (_.has(result, "index")) {
      return `Index: ${result.index}`;
    } else if (_.has(result, "key")) {
      return `${_.startCase(result.key)}: ${result.value}`;
    } else {
      return _.startCase(key);
    }
  }

  parsedAlias({ refData, key, aliasKey }) {
    if (aliasKey === "") return null;
    const aliasPath = [...key.split("."), aliasKey];
    const { value } = getValueByPath(refData, aliasPath);
    return value;
  }

  render() {
    const {
      handleRollBack,
      handleHideRules,
      isRollBackMode,
      hideList,
      groupedDiffs,
      groups = [],
      aliasKey,
      refData = {},
      index,
    } = this.props;

    const enabledRollBack = !_.isNil(handleRollBack) && isRollBackMode;

    return _.keys(groupedDiffs).map((key) => {
      const diffData = groupedDiffs[key];
      const diffKey = `${index}-${key}`;
      const isHideRule = hideList.includes(diffKey);
      let changeType = CHANGETYPE.VALUE_CHANGED;
      if (diffData.others && diffData.others.length === 1) {
        const onlyDiff = diffData.others[0];
        changeType = onlyDiff.type;
      }

      return (
        <div className="border my-2 border-gray-400" key={key}>
          <div
            className={`sticky top-0 mb-2 flex items-center gap-1 bg-gray-300 px-2 py-1 text-sm font-semibold text-gray-800 ${
              isHideRule ? `${getDisplayTypeColor(changeType)} border-l-4` : ""
            }`}
            style={{ zIndex: 1 }}
          >
            <div className="truncate">
              <span className="mr-1">{this.parseGroupKey(key)}</span>
              {this.parsedAlias({ refData, key, aliasKey })}
            </div>
            {enabledRollBack && (
              <SimpleTooltip tooltipText="Roll back rule" place="right">
                <button
                  className="hover:text-blue-600"
                  onClick={() => handleRollBack({ path: key })}
                >
                  <FiRotateCcw></FiRotateCcw>
                </button>
              </SimpleTooltip>
            )}
            <div
              className="border ml-auto flex cursor-pointer items-center gap-1 rounded-full border-gray-500 p-1 text-gray-600 hover:border-blue-600 hover:text-blue-500"
              onClick={() => handleHideRules(diffKey)}
            >
              {isHideRule ? (
                <>
                  <FaAngleDown></FaAngleDown>
                </>
              ) : (
                <>
                  <FaAngleUp></FaAngleUp>
                </>
              )}
            </div>
          </div>
          {!isHideRule && (
            <>
              {_.map(["others", ...groups], (groupKey) => {
                return (
                  <React.Fragment key={`${key}-${groupKey}`}>
                    {(diffData[groupKey] || []).length > 0 &&
                      groupKey !== "others" && (
                        <div className="border-t border-b mb-2 flex items-center gap-1 border-gray-400 px-2 py-1 font-semibold text-gray-800">
                          {this.parseGroupKey(_.last(groupKey.split(".")))}
                          {enabledRollBack && (
                            <SimpleTooltip
                              tooltipText="Roll back section"
                              place="right"
                            >
                              <button
                                className="hover:text-blue-600"
                                onClick={() =>
                                  handleRollBack({
                                    path: _.get(
                                      diffData,
                                      [groupKey, 0, "match"],
                                      null
                                    ),
                                  })
                                }
                              >
                                <FiRotateCcw></FiRotateCcw>
                              </button>
                            </SimpleTooltip>
                          )}
                        </div>
                      )}
                    <div>
                      {(diffData[groupKey] || []).map((diff, idx) => {
                        const { from, to, type, title, rest } = diff;

                        const restList = rest.split(".");
                        const parseTitle =
                          title ||
                          restList
                            .map((str) =>
                              idRegex.test(str) ? str : _.startCase(str)
                            )
                            .join(" / ");
                        return (
                          <div
                            className="border-b -mx-4 mb-2 border-gray-400 px-4 pb-2 last:border-b-0"
                            key={`${key}-${groupKey}-${idx}`}
                          >
                            <div
                              className={`${getDisplayTypeColor(
                                type
                              )} border-l-4 px-2 font-semibold text-blue-800`}
                            >
                              <div className="flex items-center gap-1">
                                <div>{parseTitle}</div>
                                {enabledRollBack && (
                                  <SimpleTooltip
                                    tooltipText="Roll back change"
                                    place="right"
                                  >
                                    <button
                                      className="hover:text-blue-600"
                                      onClick={() => handleRollBack(diff)}
                                    >
                                      <FiRotateCcw></FiRotateCcw>
                                    </button>
                                  </SimpleTooltip>
                                )}
                              </div>
                              <div className="flex text-gray-600">
                                <div
                                  className="flex break-all"
                                  style={{ width: "calc(50% - 0.5rem)" }}
                                >
                                  <div>
                                    <ConfigValueReadonly
                                      valueType={type}
                                      configValue={from}
                                    ></ConfigValueReadonly>
                                  </div>
                                </div>
                                <div className="self-center px-2 text-lg">
                                  <DiffIcon type={type} />
                                </div>

                                {getDisplayType(type) !==
                                  DISPLAYTYPE.NO_CHANGE && (
                                  <div
                                    className="flex break-all"
                                    style={{ width: "calc(50% - 0.5rem)" }}
                                  >
                                    <div className="font-semibold text-gray-800">
                                      <ConfigValueReadonly
                                        valueType={type}
                                        configValue={to}
                                        fromValue={from}
                                      ></ConfigValueReadonly>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </React.Fragment>
                );
              })}
            </>
          )}
        </div>
      );
    });
  }
}

export default ConfigDiffView;
